/**
 * Quem sabe confirmar UM cartão, por CHAVE — o índice que deixa o
 * `view-chat.vue` responder a um cartão cujo protocolo ele não conhece.
 *
 * ## O problema que ele resolve
 *
 * O canal `@respond` do contrato (§2) é genérico: o cartão emite
 * `{toolCallId, acao, payload}` e a superfície manda ao modelo a frase
 * "Confirmo" / "Cancelar". Isso basta para as write-tools do CRM, cujo
 * two-step é inteiro do servidor — o `preview_id` está guardado lá e o modelo
 * só precisa ser avisado.
 *
 * **A simulação do Fee Fixo não cabe nesse molde**, e a razão é física: os
 * `resultados` do cenário são calculados NO NAVEGADOR (o motor do Fee Lab é
 * um só) e o commit os exige. Nenhuma frase carrega um objeto de 25 chaves,
 * então existe um passo local — o depósito — que precisa acontecer ANTES da
 * frase, e a frase em si é outra (ela nomeia o `preview_id`).
 *
 * Medido em 04/09/2026, na Home IA: o consultor clicava em "Salvar cenário",
 * o "Confirmo" genérico chegava ao modelo, e ele respondia *"a confirmação
 * acontece pelo clique no cartão, na tela do Fee Lab — não por aqui no chat"*.
 * Nada era gravado, o cartão sumia e a conversa seguia como se o cenário
 * tivesse sido salvo. É a falha calada que este índice fecha.
 *
 * ## O idioma é o mesmo do resto do `ui-registry`
 *
 * **O MÓDULO se registra, o agregado só consulta** — como `registrar()` faz
 * com os cartões, `registrarExecutorDeAcao()` com o motor de chat e
 * `registrarTourDoModulo()` com o tour. É o que permite o `view-chat.vue`
 * confirmar uma simulação do Fee Fixo sem importar uma linha daquele módulo:
 * importá-lo aqui poria o chat do CRM a depender do Fee Fixo para responder a
 * um cartão, que é exatamente o acoplamento que o registry existe para
 * desfazer.
 *
 * ## O contrato do confirmador
 *
 * `confirmador({acao, dados, parte})` devolve — direto ou por Promise:
 *
 * | retorno | o que a superfície faz |
 * |---|---|
 * | `{enviar: {message, exibir}}` | manda ESSA frase ao modelo, no lugar da genérica |
 * | `{erro: <resposta>}` | mostra o snack e **não** manda frase nenhuma |
 * | `null` / `undefined` | não tratou: segue o caminho genérico ("Confirmo") |
 *
 * ★ **`{erro}` não é o mesmo que `null`.** O passo local falhou, e mandar a
 * frase mesmo assim faria o servidor recusar por um motivo que o consultor não
 * tem como entender — ele veria "não foi possível salvar" sobre uma tela que
 * mostrava os números. É a mesma ordem que o Guia já respeita
 * (`view-fee-fixo-guia-simulacao.js`): **depositar → só com `status === 200`,
 * mandar a frase**.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

const CONFIRMADORES = {}

/**
 * @param {string}   chave        A chave do cartão (`<modulo>/<cartao>`), a
 *                                MESMA de `ia/registry.js` — importe a
 *                                constante, nunca redigite: chave que não casa
 *                                não quebra nada visivelmente, só faz o
 *                                confirmador nunca ser chamado.
 * @param {Function} confirmador  `({acao, dados, parte}) => resultado`.
 * @returns {Function} desfaz o registro (útil no teste).
 */
export function registrarConfirmador(chave, confirmador) {
  if (!chave || typeof confirmador !== 'function') return () => {}
  CONFIRMADORES[chave] = confirmador
  return () => esquecerConfirmador(chave, confirmador)
}

/** Só apaga se ainda for AQUELE — mesma guarda do índice de executores. */
export function esquecerConfirmador(chave, confirmador) {
  if (!chave) return
  if (confirmador && CONFIRMADORES[chave] !== confirmador) return
  delete CONFIRMADORES[chave]
}

export function confirmadorDoCartao(chave) {
  if (!chave) return null
  return CONFIRMADORES[chave] || null
}

/** Esvazia o índice. Existe para os testes. */
export function limparConfirmadores() {
  Object.keys(CONFIRMADORES).forEach((chave) => {
    delete CONFIRMADORES[chave]
  })
}

/**
 * O passo local do cartão, quando ele tem um.
 *
 * Devolve sempre um objeto, para o chamador ficar com um `if` de uma linha:
 * `{tratado: false}` quando não há confirmador (ou ele declinou) e o caminho
 * genérico vale.
 *
 * ★ A exceção do confirmador NÃO sobe. Um erro aqui não pode estourar no meio
 * da conversa, e cair no genérico seria pior que declinar: mandaria ao modelo
 * um "Confirmo" que o servidor recusaria — com o consultor sem saber por quê.
 * O console é onde o desenvolvedor descobre; o snack, onde o consultor
 * descobre.
 *
 * @returns {Promise<{tratado: boolean, enviar?: Object, erro?: Object}>}
 */
export async function resolverConfirmacao(chave, contexto) {
  const confirmador = confirmadorDoCartao(chave)
  if (!confirmador) return { tratado: false }
  try {
    const r = await confirmador(contexto || {})
    if (!r) return { tratado: false }
    if (r.erro) return { tratado: true, erro: r.erro }
    if (r.enviar) return { tratado: true, enviar: r.enviar }
    return { tratado: false }
  } catch (err) {
    console.warn(
      '[UI_REGISTRY] o confirmador de "' + chave + '" falhou:', err
    )
    return {
      tratado: true,
      erro: {
        status: 500,
        message:
          'Não consegui concluir esta confirmação. Tente de novo em instantes.',
        value: null,
      },
    }
  }
}
